/* @flow */

import clsx from 'clsx';
import * as React from 'react';
import { connect } from 'react-redux';

import type { BasicFunction } from '../../helpers/jsHelpers/types';
import type { CombinedReducers } from '../../redux/reducers';
import type { Dispatch } from '../../redux/types/types';

import { AppProfilesBackgroundImage as backgroundImage } from '../../helpers/applicationCustomization/ui';
import Localizer from '../../helpers/localization';
import { Messenger, MessengerEvents } from '../../helpers/messenger';
import ReducedMotionManager from '../../helpers/ui/reducedMotionManager';
import { Theme, type ThemeType } from '../../helpers/ui/theme';
import { hideModal } from '../../redux/modal/actions';
import { PictoArrowLeft, PictoX } from '../pictos/Element';
import './modal.css';

type DefaultProps = {
  +ariaLabel?: string,
  +backCallback?: BasicFunction,
  +canCloseOnEscape?: boolean,
  +canCloseOnOverlayClick?: boolean,
  +className?: string | null,
  +hasBackgroundImage?: boolean,
  +hasCloseButton?: boolean,
  +hasSeparator?: boolean,
  +header?: React.Node,
  +innerRef?: ((ModalView | null) => unknown) | null,
  +isAlert?: boolean,
  +modalId?: string,
  +skipAnimation?: boolean,
  +theme?: ThemeType,
  +title?: string | null,
};

type ModalPropType = {
  ...DefaultProps,
  +children: React.Node,
};

type ReduxModalReducerStateType = {
  +isEnabled: boolean,
};

type ReduxModalDispatchToPropsType = {
  +localHideModal: BasicFunction,
};

type CompleteModalPropType = {
  ...ModalPropType,
  ...ReduxModalReducerStateType,
  ...ReduxModalDispatchToPropsType,
};

type ModalStateType = {
  closingData: any,
  isClosing: boolean,
  isClosingEnabled: boolean,
};

const InitialState = Object.freeze({
  closingData: null,
  isClosing: false,
  isClosingEnabled: true,
});

class ModalView extends React.PureComponent<CompleteModalPropType, ModalStateType> {
  abortController: AbortController;
  dialogRef: { current: HTMLDialogElement | null };
  isMouseDown: boolean;

  static defaultProps: DefaultProps = {
    ariaLabel: undefined,
    backCallback: undefined,
    canCloseOnEscape: true,
    canCloseOnOverlayClick: true,
    className: null,
    hasBackgroundImage: false,
    hasCloseButton: true,
    hasSeparator: false,
    header: null,
    innerRef: null,
    isAlert: false,
    modalId: undefined,
    skipAnimation: false,
    theme: Theme.Dark,
    title: '',
  };

  constructor(props: CompleteModalPropType) {
    super(props);

    this.abortController = new AbortController();
    this.dialogRef = React.createRef<HTMLDialogElement>();
    this.isMouseDown = false;

    this.state = { ...InitialState };
  }

  componentDidMount() {
    const { innerRef, isEnabled } = this.props;

    if (innerRef) {
      // Expose the class instance
      innerRef(this);
    }

    if (isEnabled && this.dialogRef.current && !this.dialogRef.current.open) {
      // Native method to open modal, activate backdrop, focus trap, and top-layer rendering
      this.dialogRef.current.showModal();
    }
  }

  componentDidUpdate(prevProps: CompleteModalPropType) {
    const { isEnabled } = this.props;
    const { isEnabled: prevIsEnabled } = prevProps;

    if (prevIsEnabled !== isEnabled && this.dialogRef.current) {
      if (isEnabled && !this.dialogRef.current.open) {
        // Re-opens native modal in the Top Layer when re-enabled
        this.dialogRef.current.showModal();
      } else if (!isEnabled && this.dialogRef.current.open) {
        // Closes native dialog DOM node to release Top Layer focus trap (component stays mounted in memory)
        this.dialogRef.current.close();
      }
    }
  }

  componentWillUnmount() {
    const { innerRef } = this.props;
    const { abortController } = this;

    abortController.abort('Component Modal will unmount');

    if (innerRef) {
      innerRef(null);
    }

    if (this.dialogRef.current && this.dialogRef.current.open) {
      this.dialogRef.current.close();
    }
  }

  handleNativeCancel = (event: SyntheticEvent<HTMLDialogElement>) => {
    // Intercept native ESC key behavior to let internal closing logic and animations finish
    event.preventDefault();
    event.stopPropagation();

    if (event.nativeEvent) {
      event.nativeEvent.stopImmediatePropagation();
    }

    const { canCloseOnEscape } = this.props;
    const { isClosingEnabled } = this.state;

    if (canCloseOnEscape && isClosingEnabled) {
      this.cancel();
    }
  };

  handleOverlayOnMouseDown: () => void = () => {
    this.isMouseDown = true;
  };

  handleOverlayOnMouseUp: () => void = () => {
    const { isMouseDown } = this;

    if (isMouseDown) {
      this.cancel();
      this.isMouseDown = false;
    }
  };

  close = (data: any) => {
    const { skipAnimation = false } = this.props;
    const isReduceMotionActive = ReducedMotionManager.isReduced();

    if (skipAnimation || isReduceMotionActive) {
      this.closeInternal(data);
      return;
    }

    this.setState({
      closingData: data,
      isClosing: true,
    });
  };

  cancel = () => {
    this.close();
  };

  handleCloseButtonOnClick: () => void = this.cancel;

  // eslint-disable-next-line react/no-unused-class-component-methods
  enableClosing = () => {
    this.setState({ isClosingEnabled: true });
  };

  // eslint-disable-next-line react/no-unused-class-component-methods
  disableClosing = () => {
    this.setState({ isClosingEnabled: false });
  };

  handleDialogOnMouseDown = (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  handleBackButtonOnClick = () => {
    const { backCallback } = this.props;

    if (backCallback) {
      backCallback();
    }
  };

  closeInternal = (data?: any) => {
    const { localHideModal, modalId } = this.props;
    const { closingData } = this.state;

    const localClosingData = data ?? closingData;

    if (this.dialogRef.current && this.dialogRef.current.open) {
      this.dialogRef.current.close();
    }

    localHideModal();

    const closedEvent = modalId ? `${MessengerEvents.MODAL_CLOSED}-${modalId}` : MessengerEvents.MODAL_CLOSED;
    Messenger.emit(closedEvent, localClosingData);
  };

  handleContentOnAnimationEnd = () => {
    const { isClosing } = this.state;

    if (isClosing) {
      this.closeInternal();
    }
  };

  getAnimationClass = (): string | null => {
    const { skipAnimation = false } = this.props;
    const { isClosing } = this.state;

    if (skipAnimation) {
      return null;
    }

    return isClosing ? 'slideUp' : 'slideDown';
  };

  render(): React.Node {
    const { ariaLabel, backCallback, canCloseOnOverlayClick, children, className, hasBackgroundImage, hasCloseButton, hasSeparator, header, isAlert, theme, title } = this.props;
    const { isClosingEnabled } = this.state;

    const handleOverlayOnMouseDown = canCloseOnOverlayClick && isClosingEnabled ? this.handleOverlayOnMouseDown : undefined;
    const handleOverlayOnMouseUp = canCloseOnOverlayClick && isClosingEnabled ? this.handleOverlayOnMouseUp : undefined;

    const closeButton = hasCloseButton ? <PictoX aria-label={Localizer.localize('accessibility.common.close')} className='modalClose' onClick={this.handleCloseButtonOnClick} /> : null;
    const backButton = backCallback ? <PictoArrowLeft aria-label={Localizer.localize('accessibility.navigation.back_to_card', { title: title ?? '' })} onClick={this.handleBackButtonOnClick} /> : null;

    const titleElement = (
      <div className={clsx('title', backCallback && 'clickable')} onClick={backCallback ? this.handleBackButtonOnClick : undefined}>
        {title}
      </div>
    );

    const backgroundStyle = { backgroundImage: `url(${backgroundImage})` };

    const headerElement = header !== null ? <div className={clsx('header', hasSeparator && 'separator')}>{header}</div> : null;

    return (
      <div>
        {hasBackgroundImage ? <div className='modalBackground' style={backgroundStyle} /> : null}
        <dialog
          aria-label={ariaLabel}
          aria-labelledby='modal-title'
          className={clsx('modalDialogNative', className, theme)}
          onCancel={this.handleNativeCancel}
          ref={this.dialogRef}
          role={isAlert ? 'alertdialog' : 'dialog'}
        >
          <div className='modalOverlayDiv' />
          <div className={clsx('modalContentDiv', canCloseOnOverlayClick && 'modalContentClickable')} onMouseDown={handleOverlayOnMouseDown} onMouseUp={handleOverlayOnMouseUp}>
            <div className={clsx('modalDialogDiv', className, theme)} onMouseDown={this.handleDialogOnMouseDown}>
              <div className={clsx('content', this.getAnimationClass())} onAnimationEnd={this.handleContentOnAnimationEnd}>
                <div className={clsx('modalHeader', theme)}>
                  {backButton}
                  {titleElement}
                  {closeButton}
                </div>
                {headerElement}
                {children}
              </div>
            </div>
          </div>
        </dialog>
      </div>
    );
  }
}

const mapStateToProps = (state: CombinedReducers) => {
  return {
    isEnabled: state.modal.isEnabled,
  };
};

const mapDispatchToProps = (dispatch: Dispatch): ReduxModalDispatchToPropsType => {
  return {
    localHideModal: (): void => dispatch(hideModal(false, true)),
  };
};

const Modal: React.ComponentType<ModalPropType> = connect(mapStateToProps, mapDispatchToProps)(ModalView);

export { ModalView };
export default Modal;
